import { Button, Flex, IconFont, Input, Popover, SelectDropdown } from '@byecode/ui'
import type { AutoLayout, EdgeValue, RectSizeUnit } from '@lighthouse/core'
import { findNodeDom, getFontSizeByPageStyle, PAGE_MODAL, useAtomData } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import { useImmer } from 'use-immer'

import { lastPageOfStackAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import { transform2Integer, transform2Number } from '@/utils/number'

import { SizeIcon } from '../SizeSetting/SizeIcon'
import { BASE_DROPDOWN_OPTIONS } from './LayoutSizeInput'
import { useSetPaddingHighlight } from './useSetPaddingHighlight'

interface Props {
    value?: AutoLayout['padding']
    onChange: (val: Required<AutoLayout['padding']>) => void
    disableHighlight?: boolean
    disablePercent?: boolean
    disableRem?: boolean
}

export const PaddingInput: React.FC<Props> = ({ value, onChange, disableHighlight, disablePercent, disableRem }) => {
    const unit = value?.unit || 'px'
    const initValue: EdgeValue = useMemo(() => value?.size || [0, 0, 0, 0], [value?.size])
    const [paddingValue, setPaddingValue] = useImmer<EdgeValue>(initValue)
    const [moveOpen, setMoveOpen] = useImmer<[boolean, boolean, boolean, boolean]>([false, false, false, false])

    const [left = 0, top = 0, right = 0, bottom = 0] = paddingValue

    const [open, setOpen] = useState(!!(top !== bottom || right !== left))

    useUpdateEffect(() => {
        if (isDeepEqual(initValue, paddingValue)) {
            return
        }
        setPaddingValue(initValue)
    }, [initValue])

    const handlePaddingChange = useCallback(
        (index: number, value: string) => {
            const num = value ? transform2Number(value) : undefined
            setPaddingValue(draft => {
                draft[index] = num
                if (!open) {
                    draft[index + 2] = num
                }
            })
        },
        [open, setPaddingValue]
    )

    const handleMoveChange = useCallback(
        (val: number, index: number) => {
            const v: EdgeValue = [...paddingValue]
            const num = transform2Integer(val)
            v[index] = num
            if (!open) {
                v[index + 2] = num
            }
            setPaddingValue(v)
            if (isDeepEqual(v, initValue)) {
                return
            }
            onChange({ size: v, unit })
        },
        [initValue, onChange, open, paddingValue, setPaddingValue, unit]
    )

    const handleMoveOpen = useCallback(
        (val: boolean, index: number) => {
            setMoveOpen(draft => {
                draft[index] = val
            })
        },
        [setMoveOpen]
    )

    const handleTrigger = useCallback(() => {
        if (open && (top !== bottom || left !== right)) {
            const v: EdgeValue = [left, top, left, top]
            if (isDeepEqual(v, initValue)) {
                setOpen(!open)
                return
            }
            onChange({ size: v, unit })
        }
        setOpen(!open)
    }, [bottom, initValue, left, onChange, open, right, top, unit])

    const handleSubmit = useCallback(
        (val: string, index: number) => {
            const num = transform2Number(Number(val).toFixed(2))
            const v: EdgeValue = [...paddingValue]
            v[index] = num
            if (!open) {
                v[index + 2] = num
            }
            setPaddingValue(v)
            if (isDeepEqual(v, initValue)) {
                return
            }
            onChange({ size: v, unit })
        },
        [initValue, onChange, open, paddingValue, setPaddingValue, unit]
    )

    const stackId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.stackId || '', [])
    )
    const selectedNode = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes?.[0], [])
    )

    const [unitOpen, setUnitOpen] = useState(false)
    const handleUnitChange = useCallback(
        (to: Exclude<RectSizeUnit, 'vw' | 'vh'>) => {
            if (to === unit) {
                return
            }

            const targetDom = selectedNode
                ? findNodeDom(selectedNode, document.querySelector(`.${PAGE_MODAL}`))
                : findNodeDom({ id: 'root' }, document.querySelector(`.${PAGE_MODAL}`))

            const rect = targetDom?.getBoundingClientRect()

            if (!rect || rect.width === 0) {
                onChange({ size: [left, top, right, bottom], unit: to })
                return
            }

            switch (to) {
                case '%': {
                    if (unit === 'px') {
                        const newLeft = Number(((left * 100) / rect.width).toFixed(2))
                        const newTop = Number(((top * 100) / rect.width).toFixed(2))
                        const newRight = Number(((right * 100) / rect.width).toFixed(2))
                        const newBottom = Number(((bottom * 100) / rect.width).toFixed(2))
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    } else if (unit === 'rem') {
                        const fontSize = getFontSizeByPageStyle()
                        if (!fontSize) {
                            onChange({ size: [left, top, right, bottom], unit: to })
                            return
                        }

                        const newLeft = Number(((left * fontSize * 100) / rect.width).toFixed(2))
                        const newTop = Number(((top * fontSize * 100) / rect.width).toFixed(2))
                        const newRight = Number(((right * fontSize * 100) / rect.width).toFixed(2))
                        const newBottom = Number(((bottom * fontSize * 100) / rect.width).toFixed(2))
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    }
                    break
                }
                case 'rem': {
                    const fontSize = getFontSizeByPageStyle()
                    if (!fontSize) {
                        onChange({ size: [left, top, right, bottom], unit: to })
                        return
                    }

                    if (unit === 'px') {
                        const newLeft = Number((left / fontSize).toFixed(2))
                        const newTop = Number((top / fontSize).toFixed(2))
                        const newRight = Number((right / fontSize).toFixed(2))
                        const newBottom = Number((bottom / fontSize).toFixed(2))
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    } else if (unit === '%') {
                        const newLeft = Number(((left * rect.width) / 100 / fontSize).toFixed(2))
                        const newTop = Number(((top * rect.width) / 100 / fontSize).toFixed(2))
                        const newRight = Number(((right * rect.width) / 100 / fontSize).toFixed(2))
                        const newBottom = Number(((bottom * rect.width) / 100 / fontSize).toFixed(2))
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    }
                    break
                }

                case 'px': {
                    if (unit === '%') {
                        const newLeft = Math.round((left * rect.width) / 100)
                        const newTop = Math.round((top * rect.width) / 100)
                        const newRight = Math.round((right * rect.width) / 100)
                        const newBottom = Math.round((bottom * rect.width) / 100)
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    } else if (unit === 'rem') {
                        const fontSize = getFontSizeByPageStyle()
                        if (!fontSize) {
                            onChange({ size: [left, top, right, bottom], unit: to })
                            return
                        }

                        const newLeft = Math.round(left * fontSize)
                        const newTop = Math.round(top * fontSize)
                        const newRight = Math.round(right * fontSize)
                        const newBottom = Math.round(bottom * fontSize)
                        const newEdge = [newLeft, newTop, newRight, newBottom] as const
                        setPaddingValue([...newEdge])
                        onChange({ size: [...newEdge], unit: to })
                    }
                    break
                }

                default: {
                    break
                }
            }
        },
        [bottom, left, onChange, right, selectedNode, setPaddingValue, top, unit]
    )

    const resizeStyle = useMemo(
        () =>
            moveOpen.map(item => {
                if (!item) {
                    return {}
                }
                return {
                    borderColor: 'var(--color-main)',
                    backgroundColor: 'var(--color-gray-200)'
                }
            }),
        [moveOpen]
    )

    const { onMouseEnterPadding, onMouseLeavePadding } = useSetPaddingHighlight(disableHighlight || moveOpen.some(Boolean))

    const unitIconType = useMemo(() => BASE_DROPDOWN_OPTIONS.find(item => item.value === unit)?.icon, [unit])
    const unitOptions = useMemo(() => {
        const disabledSet = new Set<Exclude<RectSizeUnit, 'vw' | 'vh'>>()
        if (disableRem) {
            disabledSet.add('rem')
        }
        if (disablePercent) {
            disabledSet.add('%')
        }
        return BASE_DROPDOWN_OPTIONS.filter(item => !disabledSet.has(item.value))
    }, [disablePercent, disableRem])

    return (
        <Flex gap={8} styles={{ root: { width: 180 } }} wrap="wrap">
            <Flex gap={8} styles={{ root: { width: 180 } }}>
                <Input
                    prefix={
                        <SizeIcon
                            type={open ? 'PaddingLeft' : 'PaddingLeftRight'}
                            min={0}
                            value={left}
                            onChangeMove={val => handleMoveOpen(val, 0)}
                            onChange={val => handleMoveChange(val, 0)}
                        />
                    }
                    styles={{
                        wrapper: resizeStyle[0]
                    }}
                    placeholder=""
                    onFocus={e => e.currentTarget.select()}
                    value={left}
                    type="number"
                    style={{ width: 65 }}
                    onChange={ev => handlePaddingChange(0, ev.target.value)}
                    onKeyDownCapture={ev => {
                        if (ev.key === 'Enter') {
                            ev.currentTarget.blur()
                        }
                    }}
                    onBlur={ev => {
                        const v = ev.target.value
                        handleSubmit(v, 0)
                    }}
                    wrapperProps={{
                        onMouseEnter: () => {
                            onMouseEnterPadding('paddingLeft')
                            if (!open) {
                                onMouseEnterPadding('paddingRight')
                            }
                        },
                        onMouseLeave: () => {
                            onMouseLeavePadding('paddingLeft')
                            if (!open) {
                                onMouseLeavePadding('paddingRight')
                            }
                        }
                    }}
                />
                <Input
                    prefix={
                        <SizeIcon
                            type={open ? 'PaddingTop' : 'PaddingTopBottom'}
                            min={0}
                            value={top}
                            onChangeMove={val => handleMoveOpen(val, 1)}
                            onChange={val => handleMoveChange(val, 1)}
                        />
                    }
                    styles={{
                        wrapper: resizeStyle[1]
                    }}
                    type="number"
                    placeholder=""
                    onFocus={e => e.currentTarget.select()}
                    value={top}
                    style={{ width: 65 }}
                    onChange={ev => handlePaddingChange(1, ev.target.value)}
                    onKeyDownCapture={ev => {
                        if (ev.key === 'Enter') {
                            ev.currentTarget.blur()
                        }
                    }}
                    onBlur={ev => {
                        const v = ev.target.value
                        handleSubmit(v, 1)
                    }}
                    wrapperProps={{
                        onMouseEnter: () => {
                            onMouseEnterPadding('paddingTop')
                            if (!open) {
                                onMouseEnterPadding('paddingBottom')
                            }
                        },
                        onMouseLeave: () => {
                            onMouseLeavePadding('paddingTop')
                            if (!open) {
                                onMouseLeavePadding('paddingBottom')
                            }
                        }
                    }}
                />
                <Button
                    type="primary"
                    style={{ backgroundColor: 'var(--color-gray-100)' }}
                    icon={<IconFont type="ArrowOpen" size={16} color="var(--color-gray-500)" />}
                    onClick={() => handleTrigger()}
                />
            </Flex>
            {open && (
                <Flex gap={8} styles={{ root: { width: 180 } }}>
                    <Input
                        prefix={
                            <SizeIcon
                                type="PaddingRight"
                                min={0}
                                value={right}
                                onChangeMove={val => handleMoveOpen(val, 2)}
                                onChange={val => handleMoveChange(val, 2)}
                            />
                        }
                        styles={{
                            wrapper: resizeStyle[2]
                        }}
                        placeholder=""
                        onFocus={e => e.currentTarget.select()}
                        value={right}
                        type="number"
                        style={{ width: 65 }}
                        onChange={ev => handlePaddingChange(2, ev.target.value)}
                        onKeyDownCapture={ev => {
                            if (ev.key === 'Enter') {
                                ev.currentTarget.blur()
                            }
                        }}
                        onBlur={ev => {
                            const v = ev.target.value
                            handleSubmit(v, 2)
                        }}
                        wrapperProps={{
                            onMouseEnter: () => {
                                onMouseEnterPadding('paddingRight')
                            },
                            onMouseLeave: () => {
                                onMouseLeavePadding('paddingRight')
                            }
                        }}
                    />
                    <Input
                        prefix={
                            <SizeIcon
                                type="PaddingBottom"
                                min={0}
                                value={bottom}
                                onChangeMove={val => handleMoveOpen(val, 3)}
                                onChange={val => handleMoveChange(val, 3)}
                            />
                        }
                        styles={{
                            wrapper: resizeStyle[3]
                        }}
                        placeholder=""
                        onFocus={e => e.currentTarget.select()}
                        value={bottom}
                        type="number"
                        style={{ width: 65 }}
                        onChange={ev => handlePaddingChange(3, ev.target.value)}
                        onKeyDownCapture={ev => {
                            if (ev.key === 'Enter') {
                                ev.currentTarget.blur()
                            }
                        }}
                        onBlur={ev => {
                            const v = ev.target.value
                            handleSubmit(v, 3)
                        }}
                        wrapperProps={{
                            onMouseEnter: () => {
                                onMouseEnterPadding('paddingBottom')
                            },
                            onMouseLeave: () => {
                                onMouseLeavePadding('paddingBottom')
                            }
                        }}
                    />
                    <Popover opened={unitOpen} onChange={setUnitOpen} width={180} position="bottom-end">
                        <Popover.Target>
                            <Button
                                type="primary"
                                style={{ backgroundColor: 'var(--color-gray-100)' }}
                                icon={unitIconType && <IconFont type={unitIconType} size={16} color="var(--color-gray-400)" />}
                            />
                        </Popover.Target>
                        <Popover.Dropdown>
                            <SelectDropdown
                                options={unitOptions}
                                onSelect={v => {
                                    handleUnitChange(v as Exclude<RectSizeUnit, 'vw' | 'vh'>)
                                    setUnitOpen(false)
                                }}
                                itemExtra={option => unit === option.value && <IconFont type="Tick" color="var(--color-main)" />}
                            />
                        </Popover.Dropdown>
                    </Popover>
                </Flex>
            )}
        </Flex>
    )
}
